import { getBootstrapData } from '@experience/core-hostapp-share-lib';
import { useAnalytics, type TConvertEventsMap } from '@experience/frontend-analytics';
import { makeUseViewTracker, makeUseClickTracker } from '@experience/vue-analytics-directives';
import { useRuntimeConfig } from 'virtual:runtime-config';
import { computed } from 'vue';

import type { EventsMap } from '@/shared/api/sogu';
import { MF_NAME } from '@/shared/consts';

const { VITE_RUNTIME_SOGU_SERVICE_URL } = useRuntimeConfig()['seo-category-listing'];

/**
 * Тип, собирающий мапу по названиям событий
 *
 * пример:
 * trackEvent('listing.pageview', { eventName: 'listing.pageview', ... })
 * Здесь параметры для второго аргумента определятся в зависимости
 * от переданного названия события
 *
 * Используется именно этот тип, так как codegen изменяет названия свойств, содержащие символы вроде . :
 */
export type ExtendedEventsMap<T> = {
  [K in keyof T as T[K] extends { eventName: infer E } | { event_name: infer E }
    ? E extends string
      ? E
      : never
    : never]: T[K];
};

/**
 * Тип параметров события, включая вариант отсутствия параметров вовсе.
 *
 * Данный тип удобен для определения типа аргумента `params` функции `trackEvent`.
 *
 * @example
 * function trackEvent<K extends keyof ExtendedEventsMap<EventsMap>>(eventName: K, params?: OptionalEventParams<K>): void {
 *   analytics.track(eventName as string, params);
 * }
 */
export type OptionalEventParams<
  E,
  K extends keyof ExtendedEventsMap<E>,
> = 'params' extends keyof ExtendedEventsMap<E>[K] ? ExtendedEventsMap<E>[K]['params'] : never;

// TODO: убрать временный тип после обновления библиотеки, используется, так как библиотека некорректно работает с типами Vue
type ParamsType = Parameters<typeof useAnalytics>[0]['params'];

const bootstrappedUserId = computed(() => getBootstrapData().userId);
const referrer = computed(() => document.referrer);
const userId = computed(() => bootstrappedUserId.value?.toString() || null);

const { trackEvent } = useAnalytics<TConvertEventsMap<EventsMap>>({
  soguURL: VITE_RUNTIME_SOGU_SERVICE_URL ?? null,
  appName: MF_NAME,
  appVersion: __APP_VERSION__,
  userId: userId as unknown as Parameters<typeof useAnalytics>[0]['userId'],
  params: computed(() => ({
    referer: referrer.value,
  })) as ParamsType,
});

export const trackEventTyped = trackEvent as Parameters<typeof makeUseViewTracker<EventsMap>>[0];

/**
 * Provides a Vue directive that implements an intersection tracker for analytics.
 *
 * @param eventName analytics event name
 * @returns vue directive
 */
export const useViewTracker = makeUseViewTracker<EventsMap>(trackEventTyped);

/**
 * Provides a Vue directive that implements a click tracker for analytics.
 *
 * @param eventName analytics event name
 * @returns vue directive
 */
export const useClickTracker = makeUseClickTracker<EventsMap>(trackEventTyped);

export { trackEvent };
